import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Upload, Plus, ChevronLeft, ChevronRight, Tag as TagIcon,
  Archive, ArchiveRestore, Download, X, Loader2,
} from "lucide-react";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { Input } from "../../../ui/input";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../../../ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../ui/alert-dialog";
import { StatusBadge } from "../components/customers/StatusBadge";
import { AddCustomerDialog } from "../components/customers/AddCustomerDialog";
import { ImportCsvDialog } from "../components/customers/ImportCsvDialog";
import { ManageTagsDialog } from "../components/customers/ManageTagsDialog";
import { CustomerStatus, formatDate } from "../../../../lib/customersEngageV1";
import { useToast } from "../../../../hooks/use-toast";
import { swatchForTag } from "../../../../lib/tagColors";

type MerchantTag = { tag: string; color: string };

type CustomerRow = {
  id: string;
  name: string | null;
  email: string;
  status: string | null;
  source: string | null;
  joined_at: string | null;
  last_activity_at: string | null;
};

type TabKey = "all" | CustomerStatus | "archived";

const PAGE_SIZE = 25;
const TABS: { value: TabKey; label: string }[] = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "unsubscribed", label: "Unsubscribed" },
  { value: "archived", label: "Archived" },
];

const Customers = () => {
  const { merchant } = useMerchant();
  const { toast } = useToast();
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [tagsByCustomer, setTagsByCustomer] = useState<Record<string, string[]>>({});
  const [merchantTags, setMerchantTags] = useState<MerchantTag[]>([]);
  const [selectionTags, setSelectionTags] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusTab, setStatusTab] = useState<TabKey>("all");
  const [tagFilter, setTagFilter] = useState<string>("__all__");
  const [page, setPage] = useState(0);

  // Selection: per-page ids OR "all in filter" mode
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectAllInFilter, setSelectAllInFilter] = useState(false);

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);

  // Bulk action UI state
  const [tagPopover, setTagPopover] = useState<"apply" | "remove" | null>(null);
  const [tagPopoverValue, setTagPopoverValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page + selection on filter change
  useEffect(() => {
    setPage(0);
    setSelected(new Set());
    setSelectAllInFilter(false);
  }, [debouncedSearch, statusTab, tagFilter]);

  // Load merchant tags master list (with colours)
  const loadAllTags = useCallback(async () => {
    if (!merchant) return;
    const [{ data: master }, { data: assigned }] = await Promise.all([
      epicengageSupabasev1.from("merchant_tags").select("tag, color").eq("merchant_id", merchant.id),
      epicengageSupabasev1.from("customer_tags").select("tag").eq("merchant_id", merchant.id),
    ]);
    const colorByTag = new Map<string, string>();
    for (const m of master ?? []) colorByTag.set(m.tag, m.color);
    // Include legacy tags only present on customer_tags
    for (const a of assigned ?? []) if (!colorByTag.has(a.tag)) colorByTag.set(a.tag, "");
    const list: MerchantTag[] = Array.from(colorByTag.entries())
      .map(([tag, color]) => ({ tag, color }))
      .sort((a, b) => a.tag.localeCompare(b.tag));
    setMerchantTags(list);
  }, [merchant]);

  const allTags = useMemo(() => merchantTags.map((t) => t.tag), [merchantTags]);
  const colorForTag = useCallback(
    (tag: string) => merchantTags.find((t) => t.tag === tag)?.color,
    [merchantTags],
  );

  useEffect(() => { loadAllTags(); }, [loadAllTags]);

  const load = useCallback(async () => {
    if (!merchant) return;
    setLoading(true);

    // Tag-filter pre-fetch (paginated to bypass Supabase's 1000-row default cap)
    let restrictIds: string[] | null = null;
    if (tagFilter !== "__all__") {
      const ids = new Set<string>();
      const CHUNK = 1000;
      let offset = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data: tagRows, error: tagErr } = await epicengageSupabasev1
          .from("customer_tags")
          .select("customer_id")
          .eq("merchant_id", merchant.id)
          .eq("tag", tagFilter)
          .range(offset, offset + CHUNK - 1);
        if (tagErr) { console.error(tagErr); break; }
        const batch = tagRows ?? [];
        for (const r of batch) ids.add(r.customer_id);
        if (batch.length < CHUNK) break;
        offset += CHUNK;
      }
      restrictIds = Array.from(ids);
      if (restrictIds.length === 0) {
        setRows([]); setTotal(0); setTagsByCustomer({}); setLoading(false); return;
      }
    }

    let query = epicengageSupabasev1
      .from("customers")
      .select("id, name, email, status, source, joined_at, last_activity_at", { count: "exact" })
      .eq("merchant_id", merchant.id);

    if (statusTab === "archived") {
      query = query.eq("archived", true);
    } else {
      query = query.eq("archived", false);
      if (statusTab !== "all") query = query.eq("status", statusTab);
    }
    if (debouncedSearch) {
      const s = debouncedSearch.replace(/[%,]/g, "");
      query = query.or(`name.ilike.%${s}%,email.ilike.%${s}%,phone.ilike.%${s}%`);
    }
    if (restrictIds) query = query.in("id", restrictIds);

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    query = query.order("joined_at", { ascending: false }).range(from, to);

    const { data, count, error } = await query;
    if (error) {
      console.error(error);
      setRows([]); setTotal(0); setLoading(false); return;
    }
    const list = (data ?? []) as CustomerRow[];
    setRows(list);
    setTotal(count ?? 0);

    // Tags for visible rows
    if (list.length) {
      const ids = list.map((r) => r.id);
      const { data: tagData } = await epicengageSupabasev1
        .from("customer_tags")
        .select("customer_id, tag")
        .eq("merchant_id", merchant.id)
        .in("customer_id", ids);
      const map: Record<string, string[]> = {};
      for (const t of tagData ?? []) {
        (map[t.customer_id] ||= []).push(t.tag);
      }
      setTagsByCustomer(map);
    } else {
      setTagsByCustomer({});
    }
    setLoading(false);
  }, [merchant, statusTab, debouncedSearch, tagFilter, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // -------- Selection helpers --------
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const togglePageSelect = () => {
    const next = new Set(selected);
    if (allOnPageSelected) rows.forEach((r) => next.delete(r.id));
    else rows.forEach((r) => next.add(r.id));
    setSelected(next);
    setSelectAllInFilter(false);
  };
  const toggleRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    setSelectAllInFilter(false);
  };
  const clearSelection = () => { setSelected(new Set()); setSelectAllInFilter(false); };

  // Effective selection count
  const selectedCount = selectAllInFilter ? total : selected.size;

  // Resolve the actual id list for a bulk action
  const resolveSelectedIds = useCallback(async (): Promise<string[]> => {
    if (!selectAllInFilter) return Array.from(selected);
    if (!merchant) return [];

    let restrictIds: string[] | null = null;
    if (tagFilter !== "__all__") {
      const { data: tagRows } = await epicengageSupabasev1
        .from("customer_tags")
        .select("customer_id")
        .eq("merchant_id", merchant.id)
        .eq("tag", tagFilter);
      restrictIds = Array.from(new Set((tagRows ?? []).map((r) => r.customer_id)));
      if (restrictIds.length === 0) return [];
    }

    let q = epicengageSupabasev1
      .from("customers")
      .select("id")
      .eq("merchant_id", merchant.id);
    if (statusTab === "archived") {
      q = q.eq("archived", true);
    } else {
      q = q.eq("archived", false);
      if (statusTab !== "all") q = q.eq("status", statusTab);
    }
    if (debouncedSearch) {
      const s = debouncedSearch.replace(/[%,]/g, "");
      q = q.or(`name.ilike.%${s}%,email.ilike.%${s}%,phone.ilike.%${s}%`);
    }
    if (restrictIds) q = q.in("id", restrictIds);

    const { data } = await q.limit(50000);
    return (data ?? []).map((r) => r.id);
  }, [selectAllInFilter, selected, merchant, tagFilter, statusTab, debouncedSearch]);

  const onSaved = () => { load(); loadAllTags(); };

  // -------- Bulk: apply / remove tag --------
  const doApplyTag = async (rawTag?: string) => {
    const tag = (rawTag ?? tagPopoverValue).trim().toLowerCase();
    if (!merchant || !tag) return;
    setBulkBusy(true);
    const ids = await resolveSelectedIds();
    if (ids.length === 0) { setBulkBusy(false); return; }
    const rowsIns = ids.map((cid) => ({ merchant_id: merchant.id, customer_id: cid, tag }));
    const { error } = await epicengageSupabasev1
      .from("customer_tags")
      .upsert(rowsIns, { onConflict: "customer_id,tag", ignoreDuplicates: true });
    setBulkBusy(false);
    setTagPopover(null); setTagPopoverValue("");
    if (error) {
      toast({ title: "Could not apply tag", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Tag applied", description: `Added "${tag}" to ${ids.length} customer${ids.length === 1 ? "" : "s"}.` });
    clearSelection();
    onSaved();
  };

  const doRemoveTag = async (rawTag?: string) => {
    const tag = (rawTag ?? tagPopoverValue).trim().toLowerCase();
    if (!merchant || !tag) return;
    setBulkBusy(true);
    const ids = await resolveSelectedIds();
    if (ids.length === 0) { setBulkBusy(false); return; }
    const chunkSize = 500;
    let removed = 0;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const { error, count } = await epicengageSupabasev1
        .from("customer_tags")
        .delete({ count: "exact" })
        .eq("merchant_id", merchant.id)
        .eq("tag", tag)
        .in("customer_id", chunk);
      if (error) {
        setBulkBusy(false);
        toast({ title: "Could not remove tag", description: error.message, variant: "destructive" });
        return;
      }
      removed += count ?? 0;
    }
    setBulkBusy(false);
    setTagPopover(null); setTagPopoverValue("");
    toast({ title: "Tag removed", description: `Removed "${tag}" from ${removed} customer${removed === 1 ? "" : "s"}.` });
    clearSelection();
    onSaved();
  };

  // Load tags currently assigned to the selection (for Remove Tag popover)
  const loadSelectionTags = useCallback(async () => {
    if (!merchant) { setSelectionTags([]); return; }
    const ids = await resolveSelectedIds();
    if (ids.length === 0) { setSelectionTags([]); return; }
    const tagSet = new Set<string>();
    const chunkSize = 500;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const { data } = await epicengageSupabasev1
        .from("customer_tags")
        .select("tag")
        .eq("merchant_id", merchant.id)
        .in("customer_id", chunk);
      for (const r of data ?? []) tagSet.add(r.tag);
    }
    setSelectionTags(Array.from(tagSet).sort());
  }, [merchant, resolveSelectedIds]);


  // -------- Bulk: archive / restore --------
  const doArchive = async () => {
    if (!merchant) return;
    setBulkBusy(true);
    const ids = await resolveSelectedIds();
    if (ids.length === 0) { setBulkBusy(false); setArchiveConfirm(false); return; }
    const isArchivedTab = statusTab === "archived";
    const chunkSize = 500;
    let updated = 0;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const { error, count } = await epicengageSupabasev1
        .from("customers")
        .update(
          isArchivedTab
            ? { archived: false, archived_at: null }
            : { archived: true, archived_at: new Date().toISOString() },
          { count: "exact" }
        )
        .eq("merchant_id", merchant.id)
        .in("id", chunk);
      if (error) {
        setBulkBusy(false);
        setArchiveConfirm(false);
        toast({ title: isArchivedTab ? "Could not restore" : "Could not archive", description: error.message, variant: "destructive" });
        return;
      }
      updated += count ?? 0;
    }
    setBulkBusy(false);
    setArchiveConfirm(false);
    toast({
      title: isArchivedTab ? "Customers restored" : "Customers archived",
      description: `${updated} customer${updated === 1 ? "" : "s"} updated.`,
    });
    clearSelection();
    load();
  };

  // -------- Bulk: export CSV --------
  const doExportCsv = async () => {
    if (!merchant) return;
    setBulkBusy(true);
    const ids = await resolveSelectedIds();
    if (ids.length === 0) { setBulkBusy(false); return; }

    // Fetch full rows + tags in chunks
    const chunkSize = 500;
    const allRows: Array<{
      name: string | null; email: string; phone: string | null; business_name: string | null;
      status: string | null; source: string | null; joined_at: string | null;
      last_activity_at: string | null; archived: boolean | null;
    } & { id: string }> = [];
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const { data, error } = await epicengageSupabasev1
        .from("customers")
        .select("id, name, email, phone, business_name, status, source, joined_at, last_activity_at, archived")
        .eq("merchant_id", merchant.id)
        .in("id", chunk);
      if (error) {
        setBulkBusy(false);
        toast({ title: "Export failed", description: error.message, variant: "destructive" });
        return;
      }
      allRows.push(...(data ?? []));
    }

    const tagMap = new Map<string, string[]>();
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const { data } = await epicengageSupabasev1
        .from("customer_tags")
        .select("customer_id, tag")
        .eq("merchant_id", merchant.id)
        .in("customer_id", chunk);
      for (const r of data ?? []) {
        if (!tagMap.has(r.customer_id)) tagMap.set(r.customer_id, []);
        tagMap.get(r.customer_id)!.push(r.tag);
      }
    }

    const headers = ["name", "email", "phone", "business_name", "status", "source", "tags", "joined_at", "last_activity_at", "archived"];
    const escape = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    for (const r of allRows) {
      lines.push([
        r.name, r.email, r.phone, r.business_name, r.status, r.source,
        (tagMap.get(r.id) ?? []).join(";"),
        r.joined_at, r.last_activity_at, r.archived ? "true" : "false",
      ].map(escape).join(","));
    }
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setBulkBusy(false);
    toast({ title: "Export ready", description: `${allRows.length} customer${allRows.length === 1 ? "" : "s"} exported.` });
  };

  const isArchivedTab = statusTab === "archived";

  return (
    <div>
      <div className="px-10 py-10 max-w-7xl pb-32">
        <header className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
            <p className="text-sm text-muted-foreground mt-1">{total.toLocaleString()} {isArchivedTab ? "archived" : "total"}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setManageTagsOpen(true)}>
              <TagIcon className="h-4 w-4" /> Manage Tags
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Import CSV
            </Button>
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" /> Add Customer
            </Button>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border mb-4 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setStatusTab(t.value)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                statusTab === t.value ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search + tag filter */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-64 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All tags" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All tags</SelectItem>
              {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Select-all-in-filter banner */}
        {selected.size > 0 && allOnPageSelected && total > rows.length && !selectAllInFilter && (
          <div className="mb-3 px-4 py-2 rounded-md border border-border bg-muted/40 text-sm flex items-center justify-between">
            <span>
              All {rows.length} customers on this page are selected.
            </span>
            <Button variant="link" size="sm" onClick={() => setSelectAllInFilter(true)}>
              Select all {total.toLocaleString()} customers in this filter
            </Button>
          </div>
        )}
        {selectAllInFilter && (
          <div className="mb-3 px-4 py-2 rounded-md border border-primary/30 bg-primary/5 text-sm flex items-center justify-between">
            <span>All {total.toLocaleString()} customers in this filter are selected.</span>
            <Button variant="link" size="sm" onClick={clearSelection}>Clear selection</Button>
          </div>
        )}

        {/* Table */}
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 w-10">
                  <Checkbox checked={allOnPageSelected} onCheckedChange={togglePageSelect} aria-label="Select all" />
                </th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3">Last activity</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">No customers found.</td></tr>
              ) : rows.map((c) => {
                const isSel = selectAllInFilter || selected.has(c.id);
                return (
                  <tr key={c.id} className="border-t border-border hover:bg-accent/30">
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={() => toggleRow(c.id)}
                        disabled={selectAllInFilter}
                        aria-label={`Select ${c.email}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{c.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{c.email}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(tagsByCustomer[c.id] ?? []).slice(0, 3).map((t) => {
                          const sw = swatchForTag(colorForTag(t));
                          return (
                            <span
                              key={t}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: sw.bg, color: sw.fg }}
                            >
                              {t}
                            </span>
                          );
                        })}
                        {(tagsByCustomer[c.id]?.length ?? 0) > 3 && (
                          <span className="text-xs text-muted-foreground">+{tagsByCustomer[c.id].length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground capitalize">{c.source ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(c.joined_at)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(c.last_activity_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button asChild variant="ghost" size="sm"><Link to={`/clientpanel/enagev1/customers/${c.id}`}>View</Link></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
          <p>{selectedCount > 0 ? `${selectedCount.toLocaleString()} selected` : `${total.toLocaleString()} customers`}</p>
          <div className="flex items-center gap-2">
            <span>Page {page + 1} of {totalPages}</span>
            <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {merchant && (
          <>
            <AddCustomerDialog
              open={addOpen}
              onOpenChange={setAddOpen}
              merchantId={merchant.id}
              tagSuggestions={allTags}
              onSaved={onSaved}
            />
            <ImportCsvDialog
              open={importOpen}
              onOpenChange={setImportOpen}
              merchantId={merchant.id}
              tagSuggestions={allTags}
              onImported={onSaved}
            />
            <ManageTagsDialog
              open={manageTagsOpen}
              onOpenChange={setManageTagsOpen}
              merchantId={merchant.id}
              onChanged={onSaved}
            />
          </>
        )}
      </div>

      {/* Floating bulk action bar */}
      {selectedCount > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-background border border-border shadow-lg rounded-full pl-4 pr-2 py-2 flex items-center gap-2">
          <span className="text-sm font-medium pr-2 border-r border-border mr-1">
            {selectedCount.toLocaleString()} selected
          </span>

          {/* Apply Tag */}
          <Popover
            open={tagPopover === "apply"}
            onOpenChange={(v) => {
              setTagPopover(v ? "apply" : null);
              if (!v) setTagPopoverValue("");
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" disabled={bulkBusy}>
                <TagIcon className="h-4 w-4" /> Apply Tag
              </Button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-72 p-3">
              <p className="text-sm font-medium mb-2">Apply tag to {selectedCount} customers</p>
              <Input
                value={tagPopoverValue}
                onChange={(e) => setTagPopoverValue(e.target.value)}
                placeholder="Search or create tag…"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doApplyTag(); } }}
                maxLength={60}
                className="h-9"
              />
              <div className="mt-2 max-h-56 overflow-y-auto -mx-1">
                {merchantTags.length === 0 ? (
                  <p className="px-2 py-3 text-xs text-muted-foreground text-center">
                    No tags yet. Create one in Manage Tags.
                  </p>
                ) : (
                  merchantTags
                    .filter((m) => m.tag.includes(tagPopoverValue.trim().toLowerCase()))
                    .slice(0, 50)
                    .map((m) => {
                      const sw = swatchForTag(m.color);
                      return (
                        <button
                          key={m.tag}
                          type="button"
                          onClick={() => doApplyTag(m.tag)}
                          disabled={bulkBusy}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent text-left disabled:opacity-50"
                        >
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ background: sw.bg, color: sw.fg }}
                          >
                            {m.tag}
                          </span>
                        </button>
                      );
                    })
                )}
              </div>
              {tagPopoverValue.trim() &&
                !merchantTags.some((m) => m.tag === tagPopoverValue.trim().toLowerCase()) && (
                  <Button
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => doApplyTag()}
                    disabled={bulkBusy}
                  >
                    {bulkBusy && <Loader2 className="h-3 w-3 animate-spin" />}
                    Create &amp; apply "{tagPopoverValue.trim().toLowerCase()}"
                  </Button>
                )}
            </PopoverContent>
          </Popover>

          {/* Remove Tag */}
          <Popover
            open={tagPopover === "remove"}
            onOpenChange={(v) => {
              setTagPopover(v ? "remove" : null);
              if (v) loadSelectionTags();
              else setTagPopoverValue("");
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" disabled={bulkBusy}>
                <X className="h-4 w-4" /> Remove Tag
              </Button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-72 p-3">
              <p className="text-sm font-medium mb-2">Remove tag from {selectedCount} customers</p>
              <div className="max-h-64 overflow-y-auto -mx-1">
                {selectionTags.length === 0 ? (
                  <p className="px-2 py-3 text-xs text-muted-foreground text-center">
                    No tags assigned to the selected customers.
                  </p>
                ) : (
                  selectionTags.map((t) => {
                    const sw = swatchForTag(colorForTag(t));
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => doRemoveTag(t)}
                        disabled={bulkBusy}
                        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-destructive/10 text-left disabled:opacity-50"
                      >
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: sw.bg, color: sw.fg }}
                        >
                          {t}
                        </span>
                        <X className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Archive / Restore */}
          <Button variant="ghost" size="sm" disabled={bulkBusy} onClick={() => setArchiveConfirm(true)}>
            {isArchivedTab ? <><ArchiveRestore className="h-4 w-4" /> Restore</> : <><Archive className="h-4 w-4" /> Archive</>}
          </Button>

          {/* Export CSV */}
          <Button variant="ghost" size="sm" disabled={bulkBusy} onClick={doExportCsv}>
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
          </Button>

          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearSelection} aria-label="Clear selection">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Archive confirm */}
      <AlertDialog open={archiveConfirm} onOpenChange={setArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArchivedTab ? `Restore ${selectedCount} customers?` : `Archive ${selectedCount} customers?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArchivedTab
                ? "Restored customers will appear in normal views and audience pickers again."
                : "Archived customers are hidden from all normal views and excluded from campaign sends. You can restore them later from the Archived tab."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doArchive(); }}
              disabled={bulkBusy}
              className={isArchivedTab ? undefined : "bg-destructive text-destructive-foreground hover:bg-destructive/90"}
            >
              {isArchivedTab ? "Restore" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Customers;
